import React, { FC, useCallback } from 'react';
import { Accordion, AccordionItem } from 'components/Accordion';
import { NavigationButton } from 'components/NavigationButton';
import { useTranslation } from 'services/localization';
import {
  selectGroupKeys,
  selectIsAllSubscriptionGroupForView,
  selectPreferencesStatus,
  selectVisibleGroup,
} from '../../preferencesSelectors';
import { showAllGroups } from '../../preferencesSlice';
import { trackNavigateToPreferencesCenterClick } from '../../services/userActionEvents';
import { GroupView } from './components/GroupView';
import { useMobileWidth } from './hooks';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import Heading from '@atlaskit/heading';
import { Box } from '@atlaskit/primitives';
import { selectPreferencesRequestsPending } from '../../../App';

const PreferenceGroup: FC = () => {
  const dispatch = useAppDispatch();
  const { translate, keyWithTitle } = useTranslation();
  const isPending = useAppSelector(selectPreferencesRequestsPending);
  const groupKeys = useAppSelector(selectGroupKeys);
  const visibleGroup = useAppSelector(selectVisibleGroup);
  const isAllGroups = useAppSelector(selectIsAllSubscriptionGroupForView);
  const { status } = useAppSelector(selectPreferencesStatus);
  const isMobile = useMobileWidth();

  const navigationClickHandler = useCallback(() => {
    trackNavigateToPreferencesCenterClick();
    dispatch(showAllGroups());
  }, [dispatch]);

  if (isPending || groupKeys.length === 0 || status !== 'ok') return null;

  const tabTitle =
    visibleGroup.tabKey != null && visibleGroup.tabKey !== 'root'
      ? `${translate(keyWithTitle(visibleGroup.tabKey))} - `
      : '';

  const sections: AccordionItem[] = groupKeys.map((key) => {
    const groupTitle = `${tabTitle} ${translate(keyWithTitle(key))}`;

    return {
      title: <Heading size={'large'}>{groupTitle}</Heading>,
      content: <GroupView groupKey={key} />,
      groupKey: key,
    };
  });

  return (
    <Box testId='preference-group-container'>
      <Accordion
        alwaysOpen={groupKeys.length === 1 || !isMobile}
        showMarker={isMobile && groupKeys.length !== 1}
        sections={sections}
      />
      <NavigationButton isVisible={!isAllGroups} onClick={navigationClickHandler} />
    </Box>
  );
};

export default PreferenceGroup;
